import pandoyLogo from '../../../assets/pandoy.png';
import { getAnalysisPanelTheme } from '../theme/themeStyles';
import { useTheme } from '../theme/ThemeContext';
import { formatReportDate, type CropCalendarReportModel } from '../utils/cropCalendarReport';
import {
  CropCalendarConfidenceMeter,
  CropCalendarGreennessChart,
  CropCalendarHarvestGauge,
  CropCalendarSeasonRail,
} from './cropCalendar/CropCalendarVisuals';

type CropCalendarReportCardProps = {
  report: CropCalendarReportModel;
  onDownload: () => void;
  onAsk?: () => void;
  isDemo?: boolean;
  chartRows?: CropCalendarReportModel['greennessRows'];
};

function cardClass(isDark: boolean) {
  return `rounded-2xl border p-3.5 ${isDark ? 'border-[#2d3748] bg-[#1c2432]' : 'border-[#e5e7eb] bg-white'}`;
}

function chipNeutral(isDark: boolean) {
  return isDark
    ? 'rounded-full border border-[#2d3748] bg-[#161b22] px-2.5 py-1 text-[12.5px] font-semibold text-[#9ca3af]'
    : 'rounded-full border border-[#e5e7eb] bg-[#f3f4f6] px-2.5 py-1 text-[12.5px] font-semibold text-[#6b7280]';
}

function chipAccent(isDark: boolean) {
  return isDark
    ? 'rounded-full bg-[#1a3324] px-2.5 py-1 text-[12.5px] font-semibold text-[#34d399]'
    : 'rounded-full bg-[#e3f2e8] px-2.5 py-1 text-[12.5px] font-semibold text-[#008540]';
}

function sectionLabel(isDark: boolean) {
  return `text-xs font-bold uppercase tracking-wide ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`;
}

function statusLabel(status: string): string {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function seasonLabel(season: string): string {
  if (!season) return '';
  return /season/i.test(season) ? season : `${season} season`;
}

export default function CropCalendarReportCard({
  report,
  onDownload,
  onAsk,
  isDemo = false,
  chartRows,
}: CropCalendarReportCardProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const greennessChartRows = chartRows && chartRows.length >= 2 ? chartRows : report.greennessRows;
  const status = isDemo ? 'Sample' : statusLabel(report.cropStatus);
  const season = seasonLabel(report.croppingSeason);

  const dateLine = [
    report.sowingDate ? `Sown ${formatReportDate(report.sowingDate)}` : null,
    report.plantingDate ? `Planted ${formatReportDate(report.plantingDate)}` : null,
    report.endDate ? `Viewed through ${formatReportDate(report.endDate)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-2.5">
      {/* Header card */}
      <article className={cardClass(isDark)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className={`text-sm font-bold ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`}>
              Crop report
            </span>
            {isDemo && <span className={chipAccent(isDark)}>DEMO</span>}
          </div>
          {status && <span className={chipAccent(isDark)}>{status}</span>}
        </div>

        <h3 className={`mt-2 truncate text-lg font-bold ${ap.heading}`}>{report.farmName}</h3>
        <p className={`text-[13px] ${ap.faint}`}>Field ID {report.farmId || '—'}</p>

        {(report.cropType || report.plantingMethod || season) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {report.cropType && <span className={chipNeutral(isDark)}>{report.cropType}</span>}
            {report.plantingMethod && <span className={chipNeutral(isDark)}>{report.plantingMethod}</span>}
            {season && <span className={chipNeutral(isDark)}>{season}</span>}
          </div>
        )}

        {dateLine && (
          <p className={`mt-2 text-[12.5px] ${ap.faint}`}>{dateLine}</p>
        )}

        <div className="mt-3 flex gap-2">
          {onAsk && (
            <button
              type="button"
              onClick={onAsk}
              aria-label="Ask Pandoy AI about this crop report"
              className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white active:scale-[0.98] ${ap.primaryBtn}`}
              style={!isDark ? { backgroundColor: '#008540' } : undefined}
            >
              <img
                src={pandoyLogo}
                alt=""
                className="h-5 w-5 shrink-0 object-contain"
                aria-hidden
              />
              Ask Pandoy AI
            </button>
          )}
          <button
            type="button"
            onClick={onDownload}
            className={`inline-flex min-h-[44px] items-center justify-center rounded-xl border px-3 text-sm font-semibold active:scale-[0.98] ${
              isDark
                ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3]'
                : 'border-[#e5e7eb] bg-white text-[#111827]'
            }`}
          >
            <i className="ri-download-line mr-1.5" />
            Download PDF
          </button>
        </div>
      </article>

      {/* Stage hero */}
      {(report.whereNow || report.inPlainWords || report.summary) && (
        <section className={cardClass(isDark)}>
          <div className={sectionLabel(isDark)}>Where your crop is now</div>
          {report.whereNow && (
            <p className={`mt-1 text-[30px] font-extrabold leading-tight ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`}>
              {report.whereNow}
            </p>
          )}
          {report.howSure && (
            <div className="mt-2">
              <CropCalendarConfidenceMeter howSure={report.howSure} isDark={isDark} />
            </div>
          )}
          {report.inPlainWords && (
            <p className={`mt-2 text-sm leading-relaxed ${ap.body}`}>{report.inPlainWords}</p>
          )}
          {report.summary && report.summary !== report.inPlainWords && (
            <p className={`mt-2 text-sm leading-relaxed ${ap.muted}`}>{report.summary}</p>
          )}
          <CropCalendarSeasonRail periods={report.stagePeriods} isDark={isDark} />
        </section>
      )}

      {/* Harvest */}
      {report.harvest.kind !== 'none' && (
        <section className={cardClass(isDark)}>
          <div className={sectionLabel(isDark)}>Harvest</div>
          <CropCalendarHarvestGauge report={report} isDark={isDark} />
        </section>
      )}

      {/* What comes next */}
      {(report.whatNext || report.tip) && (
        <section className={cardClass(isDark)}>
          <div className={sectionLabel(isDark)}>What comes next</div>
          {report.whatNext && (
            <p className={`mt-1 text-sm font-bold ${ap.heading}`}>
              {report.whatNext}
              {report.aboutWhen ? ` · ${report.aboutWhen}` : ''}
            </p>
          )}
          {report.tip && (
            <div
              className={`mt-2 rounded-xl px-3 py-2.5 text-sm ${
                isDark ? 'bg-[#3a2c0e] text-[#f0b64a]' : 'bg-[#fff1d6] text-[#9a5b00]'
              }`}
            >
              <strong>Tip:</strong> {report.tip}
            </div>
          )}
        </section>
      )}

      {/* Greenness chart */}
      {greennessChartRows.length >= 2 && (
        <section className={cardClass(isDark)}>
          <div className={sectionLabel(isDark)}>Greenness over the season</div>
          <div className="mt-2">
            <CropCalendarGreennessChart
              rows={greennessChartRows}
              stagePeriods={report.stagePeriods}
              endDate={report.endDate}
              isDark={isDark}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {report.greennessTrend.trim() && (
              <span className={chipNeutral(isDark)}>{report.greennessTrend}</span>
            )}
            {report.weatherNow && (
              <span className={chipNeutral(isDark)}>{report.weatherNow}</span>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
